import { useState } from "react";
import { toast } from "sonner";
import AdminTooltip from "./AdminTooltip";

export type WeightConfig = {
  keywordWeight: number;
  transcriptBoost: number;
  recencyBias: number;
  maxResults: number;
};

const STORAGE_KEY = "podwise_score_tuning";

export const DEFAULT_TUNING: WeightConfig = {
  keywordWeight: 1.0,
  transcriptBoost: 1.5,
  recencyBias: 0.4,
  maxResults: 3,
};

export function getStoredTuning(): WeightConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_TUNING, ...JSON.parse(stored) };
  } catch {}
  return DEFAULT_TUNING;
}

export default function AdminScoreTuning() {
  const [config, setConfig] = useState<WeightConfig>(getStoredTuning);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    toast.success("Score tuning saved — active for all searches from this browser");
  };

  const handleReset = () => {
    setConfig(DEFAULT_TUNING);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_TUNING));
    toast.success("Reset to defaults");
  };

  const sliders: { key: keyof WeightConfig; label: string; min: number; max: number; step: number; description: string }[] = [
    { key: "keywordWeight", label: "Keyword Match Weight", min: 0, max: 3, step: 0.1, description: "How much keyword matches in transcript chunks influence ranking. Higher = keyword matches matter more." },
    { key: "transcriptBoost", label: "Transcript Boost", min: 1, max: 3, step: 0.1, description: "Multiplier applied to episodes that have real transcripts vs title/description-only matches." },
    { key: "recencyBias", label: "Recency Bias", min: 0, max: 1, step: 0.05, description: "How much newer episodes are preferred over older ones. 0 = date doesn't matter, 1 = strongly prefer recent." },
    { key: "maxResults", label: "Max Results", min: 1, max: 10, step: 1, description: "Number of top results returned per search query. The AI ranker picks this many from the candidate pool." },
  ];

  return (
    <>
      <div className="mb-4">
        <h1 className="text-lg sm:text-xl font-bold mb-1">Score Tuning</h1>
        <p className="font-mono text-[11px] sm:text-sm text-muted-foreground">
          Adjust search ranking weights and scoring parameters.
          <AdminTooltip text="These parameters are sent with every search query and directly control how results are ranked and how many are returned." icon />
        </p>
      </div>

      <div className="space-y-6 max-w-lg">
        {sliders.map((s) => (
          <div key={s.key} className="retro-border bg-card p-4">
            <div className="flex items-center justify-between mb-1">
              <label className="font-mono font-bold text-xs sm:text-sm">
                {s.label}
                <AdminTooltip text={s.description} icon />
              </label>
              <span className="font-mono text-sm tabular-nums bg-muted px-2 py-0.5">{config[s.key]}</span>
            </div>
            <input
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={config[s.key]}
              onChange={(e) => setConfig((prev) => ({ ...prev, [s.key]: parseFloat(e.target.value) }))}
              className="w-full accent-primary"
            />
            <div className="flex justify-between font-mono text-[10px] text-muted-foreground mt-1">
              <span>{s.min}</span>
              <span>{s.max}</span>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            className="font-mono text-sm px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Save Configuration
          </button>
          <button
            onClick={handleReset}
            className="font-mono text-sm px-4 py-2 border border-border hover:bg-muted transition-colors text-muted-foreground"
          >
            Reset Defaults
          </button>
        </div>

        <div className="retro-border bg-muted/50 p-4">
          <p className="font-mono text-[10px] text-muted-foreground">
            <strong>Current search pipeline:</strong> Keyword matching on transcript chunks → AI re-ranking with Gemini → confidence scoring.
            These tuning parameters are sent with every search query and directly influence ranking weights and result count.
          </p>
        </div>
      </div>
    </>
  );
}
